import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Paper, 
  Typography, 
  Button, 
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Avatar,
  Fade,
  Card
} from '@mui/material';
import { 
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  People as PeopleIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  Block as BlockIcon
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import PageHeader from '../components/common/PageHeader';
import DataTable, { Column, Action } from '../components/common/DataTable';
import AlertMessage from '../components/common/AlertMessage';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { UserService } from '../services/user.service';
import { User, Role } from '../types/auth.types';
import { selectUser } from '../store/slices/authSlice';

export default function UserList() {
  const navigate = useNavigate();
  const currentUser = useSelector(selectUser);
  
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);
  
  useEffect(() => {
    setMounted(true);
    fetchUsers();
  }, []);
  
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await UserService.getAllUsers();
      setUsers(data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ошибка загрузки пользователей');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCreateUser = () => {
    navigate('/users/create');
  };
  
  const handleEditUser = (id: string) => {
    navigate(`/users/${id}`);
  };
  
  const handleDeleteUser = (id: string) => {
    if (id === currentUser?.id) {
      setError('Вы не можете удалить свою учетную запись');
      return;
    }
    
    setUserToDelete(id);
    setDeleteDialog(true);
  };
  
  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      await UserService.deleteUser(userToDelete);
      setUsers(users.filter(user => user.id !== userToDelete));
      setDeleteDialog(false);
      setUserToDelete(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ошибка удаления пользователя');
    }
  };
  
  const cancelDeleteUser = () => {
    setDeleteDialog(false);
    setUserToDelete(null);
  };
  
  const columns: Column[] = [
    {
      id: 'username',
      label: 'Логин',
    },
    {
      id: 'fullName',
      label: 'Полное имя',
    },
    {
      id: 'email',
      label: 'Email',
    },
    {
      id: 'roles',
      label: 'Роли',
      format: (roles: Role[]) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {roles.map(role => (
            <Chip 
              key={role.id} 
              label={role.name} 
              size="small" 
              sx={{
                bgcolor: 'rgba(102, 126, 234, 0.1)',
                color: '#667eea',
                fontWeight: 600,
                border: '1px solid rgba(102, 126, 234, 0.3)'
              }}
            />
          ))}
        </Box>
      ),
    },
    {
      id: 'isTwoFactorEnabled',
      label: '2FA',
      format: (value: boolean) => (
        <Chip 
          icon={value ? <SecurityIcon /> : undefined}
          label={value ? 'Включена' : 'Отключена'} 
          size="small" 
          sx={{
            bgcolor: value ? 'rgba(76, 175, 80, 0.1)' : 'rgba(158, 158, 158, 0.1)',
            color: value ? '#4caf50' : '#9e9e9e',
            fontWeight: 600,
            border: `1px solid ${value ? 'rgba(76, 175, 80, 0.3)' : 'rgba(158, 158, 158, 0.3)'}`
          }}
        />
      ),
    },
    {
      id: 'isActive',
      label: 'Статус',
      format: (value: boolean) => (
        <Chip 
          icon={value ? <CheckCircleIcon /> : <BlockIcon />}
          label={value ? 'Активен' : 'Заблокирован'} 
          size="small" 
          sx={{
            bgcolor: value ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
            color: value ? '#4caf50' : '#f44336',
            fontWeight: 600,
            border: `1px solid ${value ? 'rgba(76, 175, 80, 0.3)' : 'rgba(244, 67, 54, 0.3)'}`
          }}
        />
      ),
    },
  ];
  
  const actions: Action[] = [
    {
      icon: <EditIcon />,
      tooltip: 'Редактировать',
      onClick: handleEditUser,
      color: 'primary',
    },
    {
      icon: <DeleteIcon />,
      tooltip: 'Удалить',
      onClick: handleDeleteUser,
      color: 'error',
      disabled: (row) => row.id === currentUser?.id,
    },
  ];
  
  const activeUsersCount = users.filter(u => u.isActive).length;
  const adminUsersCount = users.filter(u => u.roles.some(r => r.name === 'ADMIN')).length;
  
  return (
    <Box>
      <Fade in={mounted} timeout={400}>
        <Box
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: 3,
            p: 4,
            mb: 3,
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '-50%',
              right: '-20%',
              width: '400px',
              height: '400px',
              background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
            }
          }}
        >
          <Box sx={{ 
            position: 'relative', 
            zIndex: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 64, height: 64 }}>
                <PeopleIcon sx={{ fontSize: 32 }} />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Пользователи
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  Управление пользователями и ролями
                </Typography>
              </Box>
            </Box>
            
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateUser}
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                px: 3,
                py: 1.5,
                fontSize: '1rem',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.3)',
                }
              }}
            >
              Создать пользователя
            </Button>
          </Box>
        </Box>
      </Fade>
      
      {error && (
        <Fade in timeout={400}>
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        </Fade>
      )}
      
      <Fade in={mounted} timeout={600}>
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Card 
            sx={{ 
              flex: 1, 
              p: 2.5, 
              borderRadius: 2,
              background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
              border: '1px solid rgba(102, 126, 234, 0.2)'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'rgba(102, 126, 234, 0.2)', color: '#667eea' }}>
                <PeopleIcon />
              </Avatar>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#667eea' }}>
                  {users.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Всего пользователей
                </Typography>
              </Box>
            </Box>
          </Card>
          
          <Card 
            sx={{ 
              flex: 1, 
              p: 2.5, 
              borderRadius: 2,
              background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(67, 160, 71, 0.1) 100%)',
              border: '1px solid rgba(76, 175, 80, 0.2)'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'rgba(76, 175, 80, 0.2)', color: '#4caf50' }}>
                <CheckCircleIcon />
              </Avatar>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#4caf50' }}>
                  {activeUsersCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Активных
                </Typography>
              </Box>
            </Box>
          </Card>
          
          <Card 
            sx={{ 
              flex: 1, 
              p: 2.5, 
              borderRadius: 2,
              background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.1) 0%, rgba(251, 140, 0, 0.1) 100%)',
              border: '1px solid rgba(255, 152, 0, 0.2)'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'rgba(255, 152, 0, 0.2)', color: '#ff9800' }}>
                <SecurityIcon />
              </Avatar>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#ff9800' }}>
                  {adminUsersCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Администраторов
                </Typography>
              </Box>
            </Box>
          </Card>
        </Box>
      </Fade>
      
      <Fade in={mounted} timeout={800}>
        <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <DataTable
            columns={columns}
            data={users}
            actions={actions}
            loading={loading}
            emptyText="Пользователи не найдены"
          />
        </Card>
      </Fade>
      
      <ConfirmDialog 
        open={deleteDialog} 
        title="Удаление пользователя" 
        message="Вы уверены, что хотите удалить этого пользователя? Это действие нельзя отменить." 
        confirmText="Удалить" 
        cancelText="Отмена" 
        onConfirm={confirmDeleteUser} 
        onCancel={cancelDeleteUser} 
        severity="error" 
      />
    </Box>
  );
}